import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SavedSearchFieldsFragment } from '__generated__/athena/graphql';
import type { MarketplaceBrowseVariables } from 'components/marketplace/browse/marketplaceBrowse';
import type { MarketplacePropertySuggestionFragment } from 'components/marketplace/map/__generated__/location-search.zeus';

import type { GeoPoint, MapLocation, MarketInfo } from '../map/types';
import { buildSaveSearchQuery, toPropertySearchQuery } from '../savedSearches/mapping';
import { canApplySavedSearchInPlace, pickPathname } from '../savedSearches/routing';
import { useSavedSearches } from '../savedSearches/useSavedSearches';
import { useMarketplaceSearch } from './useMarketplaceSearch';
import {
  buildQueryParams,
  buildVerticesQueryParams,
  isUuid,
  normalizeSearchQuery,
  normalizeSearchVertices,
  SAVED_SEARCH_ID_PARAM,
  updateQueryParams,
} from './useSearchQueries';
import type { PropertySearchQuery } from './useSearchQueries';
import type { useSearchState } from './useSearchState';
import { buildPayloadFromQuery } from './useSearchState';

type SearchState = ReturnType<typeof useSearchState>;
type SearchFilterOverride = Omit<PropertySearchQuery, 'market' | 'vertices' | 'zoneIds'>;

interface UseMarketplaceSearchControllerArgs {
  initialVariables: MarketplaceBrowseVariables;
  searchState: SearchState;
  marketInfo: MarketInfo;
  initialVertices?: GeoPoint[] | null;
  initialZoneIds?: string[] | null;
  /** `ssid` from the URL: the saved search this page view was applied from. */
  initialSavedSearchId?: string | null;
}

export const useMarketplaceSearchController = ({
  initialVariables,
  searchState,
  marketInfo,
  initialVertices,
  initialZoneIds,
  initialSavedSearchId,
}: UseMarketplaceSearchControllerArgs) => {
  const normalizedInitialVertices = normalizeSearchVertices(initialVertices);
  const {
    mapMarkers,
    listings,
    isLoading,
    isLoadingMore,
    hasError,
    clearError,
    runSearch,
    loadNextPage,
  } = useMarketplaceSearch(initialVariables);

  // Keeping "no polygon" as null lets save-search distinguish market-level
  // searches from user-drawn areas.
  const [drawnVertices, setDrawnVertices] = useState<GeoPoint[] | null>(normalizedInitialVertices);
  const [selectedLocation, setSelectedLocation] = useState<MapLocation>();
  // Vertices win over zones (the same precedence used during SSR). When the page
  // hydrates with a viewport polygon, ignore route zone ids so client searches
  // and saves keep the polygon instead of silently expanding to the whole zone.
  const [routeZoneIds, setRouteZoneIds] = useState<string[] | null>(() => {
    if (normalizedInitialVertices) {
      return null;
    }
    return initialZoneIds?.length ? initialZoneIds : null;
  });
  const [selectedAddress, setSelectedAddress] = useState<
    MarketplacePropertySuggestionFragment | undefined
  >();

  // Resolve the active id against the shared store so updates and deletions
  // propagate from every saved-search surface.
  const { savedSearches, hasLoaded: savedSearchesLoaded } = useSavedSearches();
  const [activeSavedSearchId, setActiveSavedSearchId] = useState<string | null>(
    initialSavedSearchId ?? null,
  );
  const activeSavedSearch = useMemo(
    () => savedSearches.find((saved) => saved.id === activeSavedSearchId) ?? null,
    [savedSearches, activeSavedSearchId],
  );

  const markActiveSavedSearch = useCallback((savedSearchId: string) => {
    if (!savedSearchId) {
      return;
    }
    setActiveSavedSearchId(savedSearchId);
    const params = new URLSearchParams(window.location.search);
    params.set(SAVED_SEARCH_ID_PARAM, savedSearchId);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  const clearActiveSavedSearch = useCallback(() => {
    setActiveSavedSearchId(null);
    const params = new URLSearchParams(window.location.search);
    if (!params.has(SAVED_SEARCH_ID_PARAM)) {
      return;
    }
    params.delete(SAVED_SEARCH_ID_PARAM);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  useEffect(() => {
    // Only reconcile against a genuinely loaded list; transient empties during
    // auth churn must not wipe the active reference.
    if (
      savedSearchesLoaded &&
      activeSavedSearchId &&
      !savedSearches.some((saved) => saved.id === activeSavedSearchId)
    ) {
      clearActiveSavedSearch();
    }
  }, [savedSearchesLoaded, savedSearches, activeSavedSearchId, clearActiveSavedSearch]);

  // The location the next search runs against. `buildPayloadFromQuery` treats
  // these as mutually exclusive (vertices > zoneIds > market); zone selection
  // clears `vertices` so a chosen zone wins, otherwise the current map polygon
  // (or, when empty, the bare market) drives results.
  const activeZoneIds = useMemo(() => {
    if (selectedLocation?.value) {
      return [selectedLocation.value];
    }
    return routeZoneIds?.length ? routeZoneIds : null;
  }, [routeZoneIds, selectedLocation?.value]);

  const currentSearchLocation: Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'> =
    useMemo(
      () => ({
        market: marketInfo.name,
        vertices: activeZoneIds?.length ? null : drawnVertices,
        zoneIds: activeZoneIds,
      }),
      [marketInfo.name, drawnVertices, activeZoneIds],
    );

  // Runs a search against the current location, optionally overriding filter
  // values being set in the same tick (the async filter setters wouldn't be
  // visible to `buildSearchPayload` yet). Backs both the quick and full filter
  // menus.
  const applyFilters = useCallback(
    async (filterOverride: SearchFilterOverride = {}) => {
      const status = await runSearch(
        searchState.buildSearchPayload({ ...filterOverride, ...currentSearchLocation }),
      );
      if (status !== 'applied') {
        return;
      }
      const canonicalQuery = { ...searchState.getCanonical(), ...filterOverride };
      if (activeZoneIds?.length === 1) {
        const params = buildQueryParams({ ...canonicalQuery, vertices: null, zoneIds: null });
        window.history.replaceState({}, '', `/homes/zid_${activeZoneIds[0]}?${params.toString()}`);
        return;
      }
      updateQueryParams(canonicalQuery);
    },
    [searchState, runSearch, currentSearchLocation, activeZoneIds],
  );

  // Preserve an explicit dropdown selection for retry, but commit its route
  // only after the corresponding results apply.
  const applyZoneSearch = useCallback(
    async (location: MapLocation) => {
      const zoneId = location.value;
      if (!isUuid(zoneId)) {
        return;
      }

      setSelectedLocation(location);
      setRouteZoneIds([zoneId]);
      setDrawnVertices(null);

      const status = await runSearch(
        searchState.buildSearchPayload({ zoneIds: [zoneId], vertices: null }),
      );
      if (status !== 'applied') {
        return;
      }

      const params = buildQueryParams(searchState.getCanonical(), { seedFromUrl: false });
      window.history.replaceState({}, '', `/homes/zid_${zoneId}?${params.toString()}`);
    },
    [searchState, runSearch],
  );

  const applyViewport = useCallback(
    async (coordinates: GeoPoint[]) => {
      const vertices = normalizeSearchVertices(coordinates);
      if (!vertices) {
        return;
      }
      const status = await runSearch(searchState.buildSearchPayload({ vertices }));
      // Commit the viewport only with its results so URL, list, and subsequent
      // saved-search/load-more actions stay coherent.
      if (status !== 'applied') {
        return;
      }

      const queryString = buildVerticesQueryParams(vertices);
      const pathname = marketInfo.identifier
        ? `/homes/${marketInfo.identifier}`
        : window.location.pathname;
      window.history.replaceState({}, '', `${pathname}?${queryString}`);
      updateQueryParams(searchState.getCanonical());

      setDrawnVertices(vertices);
      setSelectedLocation(undefined);
      setRouteZoneIds(null);
    },
    [searchState, runSearch, marketInfo.identifier],
  );

  const loadMore = useCallback(() => {
    if (listings?.nextPage == null) {
      return;
    }
    void loadNextPage();
  }, [listings?.nextPage, loadNextPage]);

  // Zone, polygon, and cross-market searches need fresh SSR chrome; only a
  // same-market market-level search can apply in place safely.
  const applySavedSearch = useCallback(
    async (savedSearch: SavedSearchFieldsFragment) => {
      const query = normalizeSearchQuery(toPropertySearchQuery(savedSearch));
      const definedQuery = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined),
      );

      if (!canApplySavedSearchInPlace(savedSearch, marketInfo.identifier)) {
        const params = buildQueryParams(definedQuery, { seedFromUrl: false });
        params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
        window.location.assign(`${pickPathname(savedSearch)}?${params.toString()}`);
        return;
      }

      // State setters are not visible to `buildSearchPayload` in this tick.
      const status = await runSearch(buildPayloadFromQuery(query, { market: marketInfo.name }));
      if (status !== 'applied') {
        return;
      }

      searchState.applyCanonical(query);
      setSelectedLocation(undefined);
      setSelectedAddress(undefined);
      // Only market-level searches apply in place, so the applied state has no
      // drawn polygon.
      setDrawnVertices(null);
      setRouteZoneIds(null);
      setActiveSavedSearchId(savedSearch.id);

      // A zone pathname would re-SSR the zone search on reload/share/back.
      const params = buildQueryParams(definedQuery, { seedFromUrl: false });
      params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
      window.history.replaceState({}, '', `/homes/${marketInfo.identifier}?${params.toString()}`);
    },
    [marketInfo, searchState, runSearch],
  );

  // The canonical query handed to the save-search button. `getCanonical()`
  // omits location, so the active market/zone/polygon is merged back in here.
  // We persist the routing slug (`identifier`), never the display `name`, so a
  // zone page's label isn't written into the saved search and later routed to a
  // non-existent `/homes/<label>`. Only a genuinely drawn polygon is persisted
  // as vertices — with none, the search saves as a clean market-level record.
  const getSaveSearchQuery = useCallback(
    (): PropertySearchQuery =>
      buildSaveSearchQuery({
        canonical: searchState.getCanonical(),
        marketIdentifier: marketInfo.identifier,
        selectedZoneId: selectedLocation?.value,
        routeZoneIds,
        vertices: activeZoneIds?.length ? null : drawnVertices,
      }),
    [
      searchState,
      marketInfo.identifier,
      selectedLocation?.value,
      routeZoneIds,
      activeZoneIds,
      drawnVertices,
    ],
  );

  return {
    mapMarkers,
    listings,
    isLoading,
    isLoadingMore,
    hasError,
    clearError,
    selectedLocation,
    setSelectedLocation,
    selectedAddress,
    setSelectedAddress,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
  };
};
